'use client';

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { trackSignupFunnel, trackError } from '@/lib/analytics/googleAnalytics';

const TRUST_INDICATORS = [
  { label: 'ISO 42001', icon: '🛡️', desc: 'Certified Ready' },
  { label: 'SOC 2', icon: '🔒', desc: 'Type II Compliant' },
  { label: 'Federal-Ready', icon: '🏛️', desc: 'FedRAMP Aligned' },
];

interface FieldErrors {
  fullName?: string;
  email?: string;
  password?: string;
}

interface ErrorState {
  message: string;
  type: 'duplicate_email' | 'validation' | 'general';
}

export default function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<ErrorState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const { signUp } = useAuth();
  const router = useRouter();

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const validateFields = (): boolean => {
    const errors: FieldErrors = {};
    if (!fullName.trim() || fullName.trim().length < 2) {
      errors.fullName = 'Full name must be at least 2 characters';
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const parseSignupError = (err: any): ErrorState => {
    const msg: string = err?.message || '';
    if (
      msg.toLowerCase().includes('already registered') ||
      msg.toLowerCase().includes('already exists') ||
      msg.toLowerCase().includes('duplicate') ||
      msg.toLowerCase().includes('unique constraint') ||
      msg.toLowerCase().includes('user already registered')
    ) {
      return { message: msg, type: 'duplicate_email' };
    }
    if (
      msg.toLowerCase().includes('password') ||
      msg.toLowerCase().includes('email') ||
      msg.toLowerCase().includes('invalid')
    ) {
      return { message: msg, type: 'validation' };
    }
    return { message: msg, type: 'general' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validateFields()) return;

    setLoading(true);
    trackSignupFunnel('started', { signup_method: 'email' });

    try {
      await signUp(email, password, fullName);
      trackSignupFunnel('completed', { signup_method: 'email' });
      router.push('/executive-dashboard');
    } catch (err: any) {
      const parsed = parseSignupError(err);
      setError(parsed);
      trackError('authentication', err.message, 'SIGNUP_FAILED', 'signup_page');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #020817 0%, #0F172A 40%, #0a1628 100%)' }}
    >
      {/* Grid background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(201,169,97,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Glow orbs */}
      <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-[#C9A961]/5 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-1/4 h-96 w-96 rounded-full bg-[#1E3A5F]/40 blur-3xl" />

      {/* LEFT SIDE — Branding */}
      <div className="relative hidden flex-col justify-between p-12 lg:flex lg:w-1/2">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="relative h-12 w-12 overflow-hidden rounded-xl border border-[#C9A961]/30 bg-[#1E3A5F]/60 p-1.5 shadow-lg">
            <Image
              src="/assets/images/Untitled_design-27-1772027045051.png"
              alt="ChronoMind logo"
              width={40}
              height={40}
              className="h-full w-full object-contain"
            />
          </div>
          <span className="font-heading text-xl font-bold text-white">
            Chrono<span className="text-[#C9A961]">Mind™</span>
          </span>
        </div>

        {/* Center content */}
        <div className="max-w-md">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#C9A961]/30 bg-[#C9A961]/10 px-4 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C9A961]" />
            <span className="text-xs font-medium tracking-widest text-[#C9A961] uppercase">Enterprise AI Governance</span>
          </div>

          <h1 className="mb-4 font-heading text-4xl font-bold leading-tight text-white xl:text-5xl">
            Start Your AI Governance{' '}
            <span className="bg-gradient-to-r from-[#C9A961] to-[#e8c97a] bg-clip-text text-transparent">
              Journey Today
            </span>
          </h1>
          <p className="mb-10 text-lg leading-relaxed text-gray-400">
            Join enterprise teams managing AI risk, compliance, and accountability — all in one platform.
          </p>

          {/* Trust indicators */}
          <div className="grid grid-cols-3 gap-4">
            {TRUST_INDICATORS.map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-[#C9A961]/20 bg-[#1E3A5F]/30 p-4 text-center backdrop-blur-sm"
              >
                <div className="mb-1 text-2xl">{item.icon}</div>
                <div className="text-sm font-semibold text-[#C9A961]">{item.label}</div>
                <div className="text-xs text-gray-500">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom quote */}
        <div className="rounded-xl border border-white/5 bg-white/5 p-5 backdrop-blur-sm">
          <p className="text-sm italic text-gray-400">
            &ldquo;The only platform purpose-built for enterprise AI governance at federal scale.&rdquo;
          </p>
          <p className="mt-2 text-xs font-medium text-[#C9A961]">— ChronoMind™ Executive Team</p>
        </div>
      </div>

      {/* RIGHT SIDE — Signup Form */}
      <div className="flex w-full flex-col items-center justify-center px-6 py-12 lg:w-1/2">
        {/* Mobile logo */}
        <div className="mb-8 flex items-center gap-3 lg:hidden">
          <div className="relative h-10 w-10 overflow-hidden rounded-lg border border-[#C9A961]/30 bg-[#1E3A5F]/60 p-1">
            <Image
              src="/assets/images/Untitled_design-27-1772027045051.png"
              alt="ChronoMind logo"
              width={32}
              height={32}
              className="h-full w-full object-contain"
            />
          </div>
          <span className="font-heading text-lg font-bold text-white">
            Chrono<span className="text-[#C9A961]">Mind™</span>
          </span>
        </div>

        <div className="w-full max-w-md">
          <div
            className="rounded-2xl border border-[#C9A961]/20 p-8 shadow-2xl backdrop-blur-md"
            style={{ background: 'rgba(30, 58, 95, 0.4)' }}
          >
            {/* Header */}
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-bold text-[#C9A961]">Create Your Account</h2>
              <p className="mt-1 text-sm text-gray-400">Join ChronoMind™ AI Governance Platform</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Full Name */}
              <div>
                <label htmlFor="fullName" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Full Name
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); if (fieldErrors.fullName) setFieldErrors(p => ({ ...p, fullName: undefined })); }}
                  autoComplete="name"
                  className={`w-full rounded-lg border bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 ${
                    fieldErrors.fullName
                      ? 'border-red-500/60 focus:border-red-500/60 focus:ring-red-500/20' :'border-white/10 focus:border-[#C9A961]/60 focus:ring-[#C9A961]/20'
                  }`}
                  placeholder="Jane Smith"
                />
                {fieldErrors.fullName && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.fullName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Work Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors(p => ({ ...p, email: undefined })); }}
                  autoComplete="email"
                  className={`w-full rounded-lg border bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 ${
                    fieldErrors.email
                      ? 'border-red-500/60 focus:border-red-500/60 focus:ring-red-500/20' :'border-white/10 focus:border-[#C9A961]/60 focus:ring-[#C9A961]/20'
                  }`}
                  placeholder="you@organization.com"
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (fieldErrors.password) setFieldErrors(p => ({ ...p, password: undefined })); }}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    autoComplete="new-password"
                    className={`w-full rounded-lg border bg-white/5 px-4 py-3 pr-12 text-sm text-white placeholder-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 ${
                      fieldErrors.password
                        ? 'border-red-500/60 focus:border-red-500/60 focus:ring-red-500/20' :'border-white/10 focus:border-[#C9A961]/60 focus:ring-[#C9A961]/20'
                    }`}
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 transition-colors hover:text-gray-300 focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-1 text-xs text-red-400">{fieldErrors.password}</p>
                )}

                {/* Password requirements */}
                {(passwordFocused || password.length > 0) && (
                  <div className="mt-2 grid grid-cols-2 gap-1">
                    {[
                      { key: 'length', label: '8+ characters' },
                      { key: 'uppercase', label: 'Uppercase letter' },
                      { key: 'lowercase', label: 'Lowercase letter' },
                      { key: 'number', label: 'Number' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-1">
                        <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                          passwordChecks[key as keyof typeof passwordChecks] ? 'bg-emerald-400' : 'bg-gray-600'
                        }`} />
                        <span className={`text-[10px] ${
                          passwordChecks[key as keyof typeof passwordChecks] ? 'text-emerald-400' : 'text-gray-500'
                        }`}>{label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Error messages */}
              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
                  {error.type === 'duplicate_email' ? (
                    <div>
                      <p className="text-sm font-medium text-red-400 mb-2">
                        This email is already registered.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Link
                          href="/login"
                          className="inline-flex items-center gap-1 rounded-md bg-[#C9A961] px-3 py-1.5 text-xs font-bold text-[#0F172A] hover:bg-[#e8c97a] transition-colors"
                        >
                          Sign In Instead
                        </Link>
                        <Link
                          href="/forgot-password"
                          className="inline-flex items-center gap-1 rounded-md border border-white/20 px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          Forgot Password?
                        </Link>
                      </div>
                    </div>
                  ) : error.type === 'validation' ? (
                    <div className="flex items-start gap-2.5">
                      <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs text-red-400">{error.message}</p>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start gap-2.5 mb-2">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xs text-red-400">Something went wrong. Please try again.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setError(null); handleSubmit({ preventDefault: () => {} } as any); }}
                        className="text-xs font-medium text-[#C9A961] hover:text-[#e8c97a] underline"
                      >
                        Try again
                      </button>
                      <span className="mx-2 text-gray-600 text-xs">·</span>
                      <Link href="/support" className="text-xs font-medium text-gray-400 hover:text-gray-300 underline">
                        Contact support
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full overflow-hidden rounded-lg bg-[#C9A961] px-6 py-3.5 text-sm font-bold text-[#0F172A] shadow-lg transition-all duration-300 hover:bg-[#e8c97a] hover:shadow-[0_0_20px_rgba(201,169,97,0.4)] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#C9A961] focus:ring-offset-2 focus:ring-offset-[#0F172A]"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400">
                Already have an account?{' '}
                <Link href="/login" className="font-medium text-[#C9A961] transition-colors hover:text-[#e8c97a]">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-gray-600">
            By creating an account you agree to our{' '}
            <Link href="/terms-of-service" className="text-gray-500 hover:text-gray-400 underline">Terms of Service</Link>
            {' '}and{' '}
            <Link href="/privacy-policy" className="text-gray-500 hover:text-gray-400 underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}